import {
  BarColor,
  Box,
  Card,
  TagColor as Color,
  Colors,
  Dimensions,
  Icon,
  type MediumIcon,
  Skeleton,
  StackedBar,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { F0Icon, F0TagList } from '@factorialco/f0-react'
import { Warning } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { Amount } from 'modules/project_management/components/Amount'
import { formatProjectHours } from 'modules/project_management/lib/formatHours'

type InsightCardProps = React.PropsWithChildren<{
  showOverageTag?: boolean
  title?: string
  titleGap?: Dimensions
  info?: string | React.ReactNode
  icon?: MediumIcon
  subtitle?: string
  titleColor?: Colors
  clickable?: boolean
  onClickAction?: () => void
}>

export const InsightCard = ({
  children,
  showOverageTag,
  title,
  info,
  icon,
  titleGap = 's16',
  titleColor = 'grey1000',
  clickable = false,
  onClickAction = () => null,
}: InsightCardProps) => {
  const [toggle, setToggle] = useToggle(false)

  return (
    <Card>
      <Card.Content>
        <Box width="full" flexDirection="row" flexGrow>
          <Box inline justifyContent="flexStart" width="full" gap={titleGap}>
            <Box flexDirection="row" justifyContent="spaceBetween">
              <Box
                flexDirection="row"
                alignItems="center"
                justifyContent={icon || info ? 'flexStart' : 'spaceBetween'}
                gap="s8"
              >
                {icon && <Icon.Medium icon={icon} />}
                <Text color={titleColor} size="200" weight="medium" ellipsis whiteSpace="nowrap">
                  {title}
                </Text>
                {showOverageTag && (
                  <Tag leftItem="circle" inline capitalize showCircle type={Color.radical}>
                    <Text color="grey800" size="100" weight="semibold" whiteSpace="nowrap">
                      {i18n.t('project_management.insights.tags.overage')}
                    </Text>
                  </Tag>
                )}
                {info && (
                  <Tooltip.ItemList open={toggle} toggle={setToggle} attachment="bottom center">
                    <Box width="s12">
                      <Icon.Medium icon="info-circle-line" />
                    </Box>
                    {typeof info === 'string' ? (
                      <Text size="200" color="grey000White" weight="medium">
                        {info}
                      </Text>
                    ) : (
                      info
                    )}
                  </Tooltip.ItemList>
                )}
              </Box>
              {clickable && (
                <Box
                  border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
                  borderRadius={{ all: 'abs008' }}
                  onClick={onClickAction}
                  padding="s4"
                >
                  <Icon.Small icon="chevron-right" color="grey800" />
                </Box>
              )}
            </Box>
            {children}
          </Box>
        </Box>
      </Card.Content>
    </Card>
  )
}

type HoursInsightCardProps = InsightCardProps & {
  imputedMinutes: number
  plannedMinutes?: number
}

function Hours({
  icon,
  info,
  imputedMinutes,
  plannedMinutes = 0,
  title = i18n.t('project_management.details.total_hours'),
  titleGap,
}: HoursInsightCardProps) {
  return (
    <InsightCard title={title} icon={icon} info={info} titleGap={titleGap}>
      <Box flexDirection="row" alignItems="center" gap="s8">
        <Text size="400" weight="semibold" color="grey1000">
          {formatProjectHours(imputedMinutes)}
        </Text>
        {plannedMinutes > 0 && (
          <Text size="200" weight="normal" color="grey1000" ellipsis whiteSpace="nowrap">
            {`/ ${formatProjectHours(plannedMinutes)}`}
          </Text>
        )}
      </Box>
    </InsightCard>
  )
}

type CostInsightCardProps = InsightCardProps & {
  totalCost: number
  currency: string
  plannedCost?: number
  showWarningTag?: boolean
  warningTooltipLabel?: string
  color?: Colors
}

function Cost({
  icon,
  currency,
  title = i18n.t('project_management.details.total_cost'),
  totalCost,
  plannedCost,
  info,
  showWarningTag = false,
  warningTooltipLabel,
  subtitle,
  titleGap,
  titleColor,
  color = "grey1000"
}: CostInsightCardProps) {
  const [toggle, setToggle] = useToggle(false)
  return (
    <InsightCard title={title} info={info} icon={icon} titleGap={titleGap} titleColor={titleColor}>
      <Box gap="s8">
        <Box flexDirection="row" alignItems="center" gap="s8">
          <Amount
            amount={totalCost}
            currency={currency}
            size="400"
            weight="semibold"
            color={color}
          />
          {plannedCost && (
            <Text size="200" weight="normal" color="grey1000" ellipsis whiteSpace="nowrap">
              {`/ `}
              <Amount
                amount={plannedCost}
                currency={currency}
                size="200"
                weight="normal"
                color="grey1000"
              />
            </Text>
          )}
          {showWarningTag && (
            <Box paddingTop="s6">
              <Tooltip.Dialog
                label={warningTooltipLabel}
                open={toggle}
                toggle={setToggle}
                attachment="top center"
              >
                <F0Icon icon={Warning} color="warning" />
              </Tooltip.Dialog>
            </Box>
          )}
        </Box>

        {subtitle && (
          <Text color="grey800" size="300" weight="normal" ellipsis whiteSpace="nowrap">
            {subtitle}
          </Text>
        )}
      </Box>
    </InsightCard>
  )
}

type CounterInsightCardProps = InsightCardProps & {
  totalCount: number
}

function Counter({ title, totalCount }: CounterInsightCardProps) {
  return (
    <InsightCard title={title} titleGap="s0">
      <Text color="grey1000" size="600" weight="medium" ellipsis whiteSpace="nowrap">
        {totalCount}
      </Text>
    </InsightCard>
  )
}

type TasksInsightCardProps = InsightCardProps & {
  completedTasks: number
  totalTasks: number
  overdueTasks: number
}

function Tasks({
  completedTasks,
  onClickAction,
  overdueTasks,
  title,
  titleGap,
  totalTasks,
}: TasksInsightCardProps) {
  return (
    <InsightCard title={title} titleGap={titleGap} clickable onClickAction={onClickAction}>
      <Box gap="s12" flexDirection="row" alignItems="center">
        <Text size="400" weight="semibold" color="grey1000" ellipsis whiteSpace="nowrap">
          {completedTasks}
        </Text>
        <Text size="200" weight="normal" color="grey1000" ellipsis whiteSpace="nowrap">
          /
        </Text>
        <Text size="200" weight="normal" color="grey1000" ellipsis whiteSpace="nowrap">
          {totalTasks}
        </Text>
        {overdueTasks > 0 && (
          <F0TagList
            type="alert"
            tags={[
              {
                text: i18n.t(
                  'project_management.overview.sections.project_status.insights.task_progress.overdue',
                  { count: overdueTasks }
                ),
                level: 'critical',
              },
            ]}
          />
        )}
      </Box>
    </InsightCard>
  )
}

interface StackBarProps {
  label: number
  value: number
  color: BarColor
}

type StackedBarProps = InsightCardProps & {
  totalAmount: number
  stackBars: StackBarProps[]
}

function StackedProgressBar({ title, stackBars, totalAmount }: StackedBarProps) {
  return (
    <InsightCard title={title} titleGap="s0">
      <Text weight="semibold" size="500" color="grey1000">
        {totalAmount}
      </Text>
      <StackedBar>
        {stackBars.map(({ label, value, color }) => (
          <StackedBar.Bar key={value} label={label} value={value} color={color} />
        ))}
      </StackedBar>
    </InsightCard>
  )
}

type SkeletonInsightCardProps = InsightCardProps & {
  withStackedBar?: boolean
}

function SkeletonCard({ title, withStackedBar }: SkeletonInsightCardProps) {
  return (
    <InsightCard title={title} titleGap="s0">
      <Box paddingTop="s24">
        <Skeleton width="s48" height="s8" isLoading />
      </Box>
      {withStackedBar && (
        <Box paddingTop="s40">
          <Skeleton width="s200" height="s8" isLoading />
        </Box>
      )}
    </InsightCard>
  )
}

InsightCard.Hours = Hours
InsightCard.Cost = Cost
InsightCard.Counter = Counter
InsightCard.Tasks = Tasks
InsightCard.StackedProgressBar = StackedProgressBar
InsightCard.Skeleton = SkeletonCard
